import { GraphQLClient } from 'graphql-request';
import { getSdk } from './generated/sdk';
import type {
  // Query Variables
  TranscriptsQueryVariables,
  TranscriptQueryVariables,
  UserQueryVariables,
  AppsQueryVariables,
  BitesQueryVariables,
  BiteQueryVariables,
  AnalyticsQueryVariables,
  // Mutation Variables
  CreateBiteMutationVariables,
  DeleteTranscriptMutationVariables,
  UploadAudioMutationVariables,
  AddToLiveMeetingMutationVariables,
  SetUserRoleMutationVariables,
  UpdateMeetingTitleMutationVariables,
  // Return Types
  TranscriptsQuery,
  TranscriptQuery,
  UserQuery,
  AppsQuery,
  BitesQuery,
  BiteQuery,
  AnalyticsQuery,
  CreateBiteMutation,
  DeleteTranscriptMutation,
  UploadAudioMutation,
  AddToLiveMeetingMutation,
  SetUserRoleMutation,
  UpdateMeetingTitleMutation
} from './generated/sdk';

// Export types and SDK functions
export * from './generated/sdk';

export interface FirefliesSDKConfig {
  apiKey: string;
  baseUrl?: string;
  headers?: Record<string, string>;
}

export class FirefliesSDK {
  private client: GraphQLClient;
  private sdk: ReturnType<typeof getSdk>;

  constructor(config: FirefliesSDKConfig) {
    if (!config.apiKey) {
      throw new Error('API key is required');
    }

    const { apiKey, baseUrl = 'https://api.fireflies.ai/graphql', headers = {} } = config;

    this.client = new GraphQLClient(baseUrl, {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        ...headers,
      },
    });

    this.sdk = getSdk(this.client);
  }

  /**
   * Get the raw GraphQL client for custom operations
   */
  getClient(): GraphQLClient {
    return this.client;
  }

  /**
   * Get all generated SDK methods
   */
  getSdk(): ReturnType<typeof getSdk> {
    return this.sdk;
  }

  // Convenience methods for common operations
  getTranscripts(variables?: TranscriptsQueryVariables): Promise<TranscriptsQuery> {
    return this.sdk.Transcripts(variables);
  }

  getTranscript(variables: TranscriptQueryVariables): Promise<TranscriptQuery> {
    return this.sdk.Transcript(variables);
  }

  getUser(variables?: UserQueryVariables): Promise<UserQuery> {
    return this.sdk.User(variables);
  }

  getApps(variables?: AppsQueryVariables): Promise<AppsQuery> {
    return this.sdk.Apps(variables);
  }

  getBites(variables?: BitesQueryVariables): Promise<BitesQuery> {
    return this.sdk.Bites(variables);
  }

  getBite(variables: BiteQueryVariables): Promise<BiteQuery> {
    return this.sdk.Bite(variables);
  }

  getAnalytics(variables?: AnalyticsQueryVariables): Promise<AnalyticsQuery> {
    return this.sdk.Analytics(variables);
  }

  createBite(variables: CreateBiteMutationVariables): Promise<CreateBiteMutation> {
    return this.sdk.CreateBite(variables);
  }

  deleteTranscript(variables: DeleteTranscriptMutationVariables): Promise<DeleteTranscriptMutation> {
    return this.sdk.DeleteTranscript(variables);
  }

  uploadAudio(variables?: UploadAudioMutationVariables): Promise<UploadAudioMutation> {
    return this.sdk.UploadAudio(variables);
  }

  addToLiveMeeting(variables: AddToLiveMeetingMutationVariables): Promise<AddToLiveMeetingMutation> {
    return this.sdk.AddToLiveMeeting(variables);
  }

  setUserRole(variables: SetUserRoleMutationVariables): Promise<SetUserRoleMutation> {
    return this.sdk.SetUserRole(variables);
  }

  updateMeetingTitle(variables: UpdateMeetingTitleMutationVariables): Promise<UpdateMeetingTitleMutation> {
    return this.sdk.UpdateMeetingTitle(variables);
  }
}

/**
 * Create a new Fireflies SDK instance
 */
export function createFirefliesSDK(config: FirefliesSDKConfig): FirefliesSDK {
  return new FirefliesSDK(config);
}

/**
 * Create a simple GraphQL client with authentication
 */
export function createClient(config: FirefliesSDKConfig): ReturnType<typeof getSdk> {
  if (!config.apiKey) {
    throw new Error('API key is required');
  }

  const { apiKey, baseUrl = 'https://api.fireflies.ai/graphql', headers = {} } = config;

  const client = new GraphQLClient(baseUrl, {
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  return getSdk(client);
} 